import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import { tooltipContainerStyles, tooltipContentStyles } from './Tooltip.styles';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export type TooltipProps = {
  /**
   * Content to display in the tooltip
   */
  content: React.ReactNode;
  /**
   * Preferred position of the tooltip relative to the children.
   * May flip to stay in viewport.
   */
  position?: TooltipPosition;
  /**
   * Child element that triggers the tooltip
   */
  children: React.ReactNode;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Delay in ms before showing tooltip (default: 200)
   */
  delay?: number;
  /**
   * When true, hover does not show the tooltip (e.g. text is not truncated).
   */
  disabled?: boolean;
  /**
   * Stretch the trigger wrapper to full width (needed for truncated text blocks).
   */
  fullWidth?: boolean;
  /** Allow pointer/touch activation in addition to hover and focus. */
  openOnClick?: boolean;
};

const Tooltip = ({
  content,
  position = 'top',
  children,
  className,
  delay = 200,
  disabled = false,
  fullWidth = false,
  openOnClick = false,
}: TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && !disabled,
    onOpenChange: (open) => {
      if (!disabled) {
        setIsOpen(open);
      }
    },
    placement: position,
    middleware: [
      offset(8),
      flip(),
      shift({ padding: 8 }),
      arrow({ element: arrowRef, padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: !disabled,
    delay: { open: delay, close: 0 },
    move: false,
  });
  const focus = useFocus(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const click = useClick(context, { enabled: !disabled && openOnClick });
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role,
  ]);

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 200,
  });

  return (
    <>
      <div
        ref={refs.setReference}
        css={tooltipContainerStyles(fullWidth)}
        className={className}
        {...getReferenceProps()}
      >
        {children}
      </div>
      {isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            css={tooltipContentStyles}
            style={{ ...floatingStyles, ...transitionStyles }}
            {...getFloatingProps()}
          >
            {content}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              width={10}
              height={5}
              strokeWidth={1}
              fill="var(--color-neutral-900)"
              stroke="var(--color-neutral-900)"
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

Tooltip.displayName = 'Tooltip';

export default Tooltip;
